import { action, observable, makeObservable } from "mobx";
import TaskTrackerAPI from "../api/TaskTracker";
import { GetTasksParams, Task } from "../models/TaskTracker/Task";
import { OrderProducts } from "../models/TaskTracker/OrderProducts";
import { Admin } from "../models/TaskTracker/Admin";
import { TaskComment } from "../models/TaskTracker/TaskComment";
import { TaskCommentAttachment } from "../models/TaskTracker/TaskCommentAttachment";

export class TaskTrackerStore {
  TaskTrackerAPI: TaskTrackerAPI;
  limit: number;
  total_pages: number;
  total_count: number;
  tasks: Task[] = [];
  orderIds: number[];
  products: OrderProducts[];
  admins: Admin[];
  taskDetails: Task | undefined;
  taskComments: TaskComment[] = [];
  loadingComments: boolean = false;
  submitting: boolean = false;
  deleteTargetId: number | null = null;
  refreshStates: {
    taskTracker: boolean;
  };

  constructor() {
    this.TaskTrackerAPI = new TaskTrackerAPI();
    this.tasks = [];
    this.limit = 0;
    this.total_pages = 0;
    this.total_count = 0;
    this.orderIds = [];
    this.products = [];
    this.admins = [];
    this.taskDetails = {};
    this.taskComments = [];
    this.loadingComments = false;
    this.submitting = false;
    this.deleteTargetId = null;
    this.refreshStates = {
      taskTracker: false,
    };

    makeObservable(this, {
      tasks: observable,
      orderIds: observable,
      products: observable,
      admins: observable,
      taskDetails: observable,
      taskComments: observable,
      loadingComments: observable,
      submitting: observable,
      deleteTargetId: observable,
      setSubmitting: action,
      setDeleteTargetId: action,
      getTasks: action,
      refreshStates: observable,
      setRefreshState: action,
      getProjectOrderIds: action,
      getTaskComments: action,
      addTaskComment: action,
      editTaskComment: action,
      deleteTaskComment: action,
    });
  }

  setSubmitting = (value: boolean) => {
    this.submitting = value;
  };

  setDeleteTargetId = (value: number | null) => {
    this.deleteTargetId = value;
  };

  getTasks = async (params: GetTasksParams) => {
    try {
      const response = await this.TaskTrackerAPI.getTasks(params);
      this.limit = response.limit;
      this.total_pages = response.total_pages;
      this.total_count = response.total_count;
      this.tasks = response.list;
    } catch (error: any) {
      throw error;
    }
  };

  getProjectOrderIds = async (order_id: number) => {
    try {
      const response = await this.TaskTrackerAPI.getProjectOrders(order_id);
      this.orderIds = response.result || [];
    } catch (error: any) {
      throw error;
    }
  };

  getOrderProducts = async (order_id: number) => {
    try {
      const response = await this.TaskTrackerAPI.getProducts(order_id);
      this.products = response.result || [];
    } catch (error: any) {
      throw error;
    }
  };

  getAdminBySearch = async (name: string) => {
    try {
      const response = await this.TaskTrackerAPI.getAdmin(name);
      this.admins = response.result.list || [];
    } catch (error: any) {
      throw error;
    }
  };

  createTask = async (taskDetails: Task) => {
    try {
      const response = await this.TaskTrackerAPI.createTask(taskDetails);
      this.admins = response.result.list || [];
    } catch (error: any) {
      throw error;
    }
  };

  getTaskDetailsById = async (taskId: number) => {
    try {
      const response = await this.TaskTrackerAPI.getTaskDetails(taskId);
      this.taskDetails = response || {};
    } catch (error: any) {
      throw error;
    }
  };

  updateTaskDetails = async (updatedTask: Task) => {
    try {
      const response = await this.TaskTrackerAPI.updateTask(updatedTask);
      const updatedTaskDetails =
        response.result && typeof response.result === "object"
          ? { ...updatedTask, ...response.result }
          : updatedTask;

      if (this.taskDetails?.id === updatedTask.id) {
        this.taskDetails = { ...this.taskDetails, ...updatedTaskDetails };
      }

      const taskIndex = this.tasks.findIndex(
        (task) => task.id === updatedTask.id
      );
      if (taskIndex !== -1) {
        this.tasks[taskIndex] = {
          ...this.tasks[taskIndex],
          ...updatedTaskDetails,
        };
      }

      return response.result;
    } catch (error: any) {
      throw error;
    }
  };
  getTaskComments = async (taskId: number) => {
    try {
      this.loadingComments = true;
      const response = await this.TaskTrackerAPI.getTaskComments(taskId);
      this.taskComments = response?.list || [];
      return this.taskComments;
    } catch (error: any) {
      throw error;
    } finally {
      this.loadingComments = false;
    }
  };

  addTaskComment = async (
    taskId: number,
    message: string,
    parentId?: number,
    attachments?: string[],
    author?: { id: number; name?: string }
  ) => {
    try {
      const response = await this.TaskTrackerAPI.addTaskComment(
        taskId,
        message,
        parentId,
        attachments
      );
      const commentId = Number(response?.result);
      if (!commentId) return;

      const now = new Date().toISOString();
      const attachmentList: TaskCommentAttachment[] = (attachments || []).map(
        (file_path) => ({
          file_path,
          uploaded_by: author?.id,
          uploaded_by_name: author?.name,
        })
      );

      const newComment: TaskComment = {
        id: commentId,
        task_id: taskId,
        message,
        admin_id: author?.id ?? 0,
        admin_name: author?.name,
        parent_id: parentId ?? null,
        is_edited: 0,
        active: 1,
        created_date: now,
        updated_date: null,
        attachments: attachmentList,
      };
      
      this.taskComments = [newComment, ...this.taskComments];
    } catch (error: any) {
      throw error;
    }
  };

  editTaskComment = async (
    taskId: number,
    commentId: number,
    message: string
  ) => {
    try {
      await this.TaskTrackerAPI.editTaskComment(taskId, commentId, message);
      const now = new Date().toISOString();
      this.taskComments = this.taskComments.map((comment) =>
        comment.id === commentId
          ? {
            ...comment,
            message,
            is_edited: 1,
            updated_date: now,
          }
          : comment
      );
    } catch (error: any) {
      throw error;
    }
  };

  deleteTaskComment = async (taskId: number, commentId: number) => {
    try {
      await this.TaskTrackerAPI.deleteTaskComment(taskId, commentId);
      const now = new Date().toISOString();
      this.taskComments = this.taskComments.map((comment) =>
        comment.id === commentId
          ? {
            ...comment,
            active: 0,
            message: "",
            updated_date: now,
          }
          : comment
      );
    } catch (error: any) {
      throw error;
    }
  };

  setRefreshState = (type: keyof typeof this.refreshStates, value: boolean) => {
    this.refreshStates[type] = value;
  };
  refreshTaskTracker = async (
    orderId: number,
    taskTrackerParams: {
      page_no: number;
      limit: number;
      sortBy: string;
      sortType: string;
      status?: number | null;
      owner_id?: number[];
      assignee_id?: number[];
      search_text?: string;
    },
    TaskTrackerStore: any
  ) => {
    if (!orderId) return;

    this.setRefreshState("taskTracker", true);

    try {
      await TaskTrackerStore?.getTasks({
        order_id: [orderId],
        page_no: taskTrackerParams.page_no,
        limit: taskTrackerParams.limit,
        sortBy: taskTrackerParams.sortBy,
        sortType: taskTrackerParams.sortType,
        status: taskTrackerParams.status
          ? [taskTrackerParams.status]
          : undefined,
        owner_id: taskTrackerParams.owner_id && taskTrackerParams.owner_id.length > 0
          ? taskTrackerParams.owner_id
          : undefined,
        assignee_id: taskTrackerParams.assignee_id && taskTrackerParams.assignee_id.length > 0
          ? taskTrackerParams.assignee_id
          : undefined,
        search_text: taskTrackerParams.search_text || undefined,
      });
    } catch (error) {
    } finally {
      setTimeout(() => {
        this.setRefreshState("taskTracker", false);
      }, 500);
    }
  };
}
